import React from "react";
import CustomButton from "../CustomButton";
import { addWorkstreamLinks, addProjectLinks, updateWorkstreamLinks, updateProjectLinks } from "../../api/Api";
import { showToast } from "../../utils/toastUtils";
import ModalUI from "../ui/ModalUI";
import { useFormik } from "formik";
import * as Yup from "yup";
import RichTextEditor from "../texts/RichTextEditor";
import TextAreaInput from './../Inputs/TextAreaInput';
import ShortTextInput from './../Inputs/ShortTextInput';

function AddUpdateLinkModal({ show, handleClose, entityType, entityId, refreshLinks, link }) {
  return (
    <ModalUI show={show} handleClose={handleClose} title={link ? "Update Link" : "Add New Link"}>
      <AddUpdateLinkForm
        handleClose={handleClose}
        entityType={entityType}
        entityId={entityId}
        refreshLinks={refreshLinks}
        link={link} // Pass link for editing if available
      />
    </ModalUI>
  );
}

export default AddUpdateLinkModal;

function AddUpdateLinkForm({ handleClose, entityType, entityId, refreshLinks, link }) {
  const formik = useFormik({
    initialValues: {
      title: link?.title || "", // Populate with link's data if editing
      url: link?.url || "", // Populate with link's data if editing
      description: link?.description || "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required")
       .max(255, "Title cannot be more than 255 characters"),
      url: Yup.string().url("Enter a valid URL").required("Link is required")
       .max(2000, "URL cannot be more than 2000 characters"),
      description: Yup.string().required("Description is required"),
    }),

    onSubmit: async (values, { setSubmitting }) => {
      try {
        let response;
        if (link) {
          // Update link if editing
          if (entityType === "initiative") {
            response = await updateWorkstreamLinks(entityId, link.id, {
              title: values.title,
              url: values.url,
              description: values.description,
            });
          } else if (entityType === "project") {
            response = await updateProjectLinks(entityId, link.id, {
              title: values.title,
              url: values.url,
              description: values.description,
            });
          }
        } else {
          // Add new link
          if (entityType === "initiative") {
            response = await addWorkstreamLinks(entityId, {
              title: values.title,
              url: values.url,
              description: values.description,
            });
          } else if (entityType === "project") {
            response = await addProjectLinks(entityId, {
              title: values.title,
              url: values.url,
              description: values.description,
            });
          }
        }

        if (!response || (response.status !== 200 && response.status !== 201)) {
          throw new Error(link ? "Failed to update link" : "Failed to add link");
        }

        showToast(link ? "Link updated successfully" : "Link added successfully", "success");
        handleClose();
        if (refreshLinks) refreshLinks();
      } catch (error) {
        showToast(link ? "Failed to update link" : "Failed to add link", "error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <div >
        <ShortTextInput
          label={"Title"}
          type="text"
          id="title"
          name="title"
          value={formik.values.title}
          onChange={formik.handleChange}
          placeholder="Enter a Title"
          className="form-control"
          error={formik.touched.title && formik.errors.title}
        />
        
      </div>
      <div className="mt-3">

        <ShortTextInput
          label={"Link"}
          type="url"
          id="url"
          name="url"
          value={formik.values.url}
          onChange={formik.handleChange}
          placeholder="Enter a Link"
          className="form-control"
          error={formik.touched.url && formik.errors.url}
        />
      

      </div>

      <div className="mt-3">
        <TextAreaInput
          label={"Description"}
          value={formik.values.description}
          onChange={(e) => formik.setFieldValue("description", e.target.value)}
          error={formik.touched.description && formik.errors.description}
        />

      </div>

      <div className=" d-flex align-items-center justify-content-center mt-4 pb-3">
        <CustomButton type="submit" disabled={formik.isSubmitting}>
          {formik.isSubmitting ? (link ? "Updating..." : "Adding...") : link ? "Update" : "Add"}
        </CustomButton>
      </div>
    </form>
  );
}

export { AddUpdateLinkForm };
